/* eslint-disable no-console */
import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { AnalyzeDocumentsInput, AnalyzeDocumentsResponse } from '@/types/analysis';

// Global state to prevent multiple draft creations across component instances
// @ts-expect-error - Used in conditional logic below
const _globalDraftCreationState = {
  isCreating: false,
  lastDocumentsKey: null as string | null,
  createdDrafts: new Set<string>(), // Track created drafts by documents key
};

interface UseAIAnalysisProps {
  campaignId: string | null;
  cvPath?: string;
  jobDescriptionPath?: string;
  jobDescriptionLink?: string;
  jobDescriptionText?: string;
  onDataExtracted: (data: any) => void;
  skipAutoAnalysis?: boolean;
  existingAnalysisData?: any;
  onEnsureDraft?: () => Promise<void>;
}

export const useAIAnalysis = ({
  campaignId,
  cvPath,
  jobDescriptionPath,
  jobDescriptionLink,
  jobDescriptionText,
  onDataExtracted,
  skipAutoAnalysis = false,
  existingAnalysisData = null,
  onEnsureDraft,
}: UseAIAnalysisProps) => {
  const { toast } = useToast();
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisCompleted, setAnalysisCompleted] = useState(!!existingAnalysisData);
  const [analysisError, setAnalysisError] = useState<string | null>(null);

  // Protection contre les appels multiples - utiliser une clé unique basée sur les documents
  const documentsKey = `${cvPath || ''}-${jobDescriptionPath || ''}-${jobDescriptionLink || ''}-${jobDescriptionText?.substring(0, 50) || ''}`;
  const hasTriggeredDraftRef = useRef<string | null>(null);
  // @ts-expect-error - Used in draft creation logic
  const _isCreatingDraftRef = useRef(false);

  // Si nous avons des données existantes, les appliquer immédiatement
  useEffect(() => {
    if (existingAnalysisData && !analysisCompleted) {
      console.log('✅ Données d\'analyse existantes trouvées, application immédiate');
      onDataExtracted(existingAnalysisData);
      setAnalysisCompleted(true);
    }
  }, [existingAnalysisData, analysisCompleted, onDataExtracted]);

  const hasRequiredDocuments = cvPath && (jobDescriptionPath || jobDescriptionLink || jobDescriptionText) && cvPath.trim() !== '' && ((jobDescriptionPath && jobDescriptionPath.trim() !== '') || (jobDescriptionLink && jobDescriptionLink.trim() !== '') || (jobDescriptionText && jobDescriptionText.trim() !== ''));

  const triggerAnalysis = useCallback(async () => {
    console.log('🔍 triggerAnalysis appelé avec:', {
      hasRequiredDocuments,
      campaignId,
      cvPath,
      jobDescriptionPath,
      jobDescriptionLink,
      jobDescriptionText: jobDescriptionText?.length || 0,
      cvPathValid: cvPath && cvPath.trim() !== '',
      jobDescValid: (jobDescriptionPath && jobDescriptionPath.trim() !== '') || (jobDescriptionLink && jobDescriptionLink.trim() !== '') || (jobDescriptionText && jobDescriptionText.trim() !== '')
    });

    if (!hasRequiredDocuments || !campaignId) {
      console.log('❌ Analyse IA - Documents manquants ou campaignId manquant');
      return;
    }

    console.log('🚀 Déclenchement analyse IA avec nouvelle architecture unpdf + GPT-4o', {
      campaignId,
      cvPath,
      jobDescriptionPath,
      jobDescriptionLink,
      jobDescriptionText: jobDescriptionText?.length || 0
    });
    setIsAnalyzing(true);
    setAnalysisError(null);

    try {
      // Vérifier la session utilisateur
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !sessionData?.session) {
        throw new Error('Vous devez être connecté pour effectuer cette analyse');
      }

      console.log('✅ Session utilisateur valide');

      // Préparer l'input pour la nouvelle Edge Function
      const analysisInput: AnalyzeDocumentsInput = {
        campaignId,
        cvPath: cvPath,
        cvUrl: cvPath, // Support both names for compatibility
        jobDescriptionPath: jobDescriptionPath,
        jobDescriptionUrl: jobDescriptionPath, // Support both names for compatibility
        jobDescriptionText,
        jobDescriptionLink,
      };

      console.log('📤 Envoi vers nouvelle Edge Function:', analysisInput);

      // Appeler la nouvelle Edge Function
      const { data, error } = await supabase.functions.invoke('analyze-campaign-documents', {
        body: analysisInput,
      });

      console.log('📡 Réponse de la nouvelle fonction:', { data, error });

      if (error) {
        console.error('❌ Erreur edge function:', error);
        throw error;
      }

      const response = data as AnalyzeDocumentsResponse;

      if (response?.success && response.data) {
        
        onDataExtracted(response.data);
        setAnalysisCompleted(true);
        toast({
          title: "Analyse terminée",
          description: "L'IA a extrait les informations des documents avec la nouvelle architecture optimisée.",
        });
      } else {
        console.error('❌ Réponse edge function sans succès:', response);
        throw new Error(response?.error || 'Erreur lors de l\'analyse');
      }
    } catch (error: any) {
      console.error('❌ Erreur analyse IA:', error);
      console.error('❌ Error stack:', error.stack);

      // Gestion d'erreurs améliorée
      let errorMessage = error.message || "Une erreur est survenue lors de l'analyse.";
      let errorTitle = "Erreur d'analyse";

      // Erreurs spécifiques à la nouvelle architecture
      if (error.message?.includes('unpdf')) {
        errorTitle = "Erreur de traitement PDF";
        errorMessage = "Impossible de lire le fichier PDF. Vérifiez que le fichier n'est pas corrompu ou protégé.";
      } else if (error.message?.includes('OpenAI API error')) {
        errorTitle = "Erreur d'analyse IA";
        errorMessage = "Erreur lors de l'analyse par l'IA. Veuillez réessayer dans quelques instants.";
      } else if (error.message?.includes('CV path manquant')) {
        errorTitle = "CV manquant";
        errorMessage = "Veuillez d'abord télécharger un CV.";
      } else if (error.message?.includes('Aucune fiche de poste')) {
        errorTitle = "Fiche de poste manquante";
        errorMessage = "Veuillez fournir une fiche de poste (texte ou fichier PDF).";
      } else if (error.message?.includes('Edge Function returned a non-2xx status code')) {
        errorTitle = "Erreur serveur";
        errorMessage = "Une erreur s'est produite sur le serveur. Vérifiez vos fichiers et réessayez.";
      }

      setAnalysisError(errorMessage);
      toast({
        title: errorTitle,
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsAnalyzing(false);
    }
  }, [campaignId, cvPath, jobDescriptionPath, jobDescriptionLink, jobDescriptionText, hasRequiredDocuments, onDataExtracted, toast]);

  // Auto-trigger analysis when documents are ready
  useEffect(() => {
    const handleAnalysis = async () => {
      // Reset trigger flag when campaignId changes from null to valid ID
      if (campaignId && hasTriggeredDraftRef.current) {
        hasTriggeredDraftRef.current = null;
      }

      // Ne déclencher l'analyse automatique que si skipAutoAnalysis est false
      if (hasRequiredDocuments && campaignId && !isAnalyzing && !analysisCompleted && !analysisError && !skipAutoAnalysis && !existingAnalysisData) {
        
        triggerAnalysis();
      }
    };

    handleAnalysis();
  }, [hasRequiredDocuments, campaignId, isAnalyzing, analysisCompleted, analysisError, skipAutoAnalysis, existingAnalysisData, documentsKey, onEnsureDraft, triggerAnalysis]);

  return {
    isAnalyzing,
    analysisCompleted,
    analysisError,
    triggerAnalysis,
    hasRequiredDocuments,
  };
};